import { buildGrantUrl } from 'components/nylas/v3/grant/helpers';
import { scopesFor } from 'components/nylas/v3/grant/scopes';

import type { ProviderName } from 'components/nylas/v3/types';
import type { ApiError, EmailFile, EmailFileReference, SendEmailConfig } from './types';

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

interface EmailPayload {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
}

export function splitEmails(value: string): string[] {
  return value.split(',').map(email => email.trim()).filter(Boolean);
}

export function errorMessage(error: ApiError): string {
  const { status, data } = error.response ?? {};
  const code = data?.error;

  if (status === 403 && code === 'reauthentication_required') {
    return 'Your email connection needs to be reconnected before you can send. ' +
      'Reconnect it from your integration settings and try again.';
  }
  if (code === 'duplicate_send') {
    return 'This email has already been sent.';
  }
  if (code === 'not_enabled') {
    return 'Sending email is not enabled for your account.';
  }

  return code || 'Something went wrong sending your email. Please try again.';
}

export function buildRequestBody(
  { to, cc, bcc, subject, body }: EmailPayload,
  files: EmailFile[],
  idempotencyKey: string
): FormData | object {
  if (files.length === 0) {
    return { to, cc, bcc, subject, body, idempotencyKey };
  }

  const data = new FormData();
  to.forEach(email => data.append('to[]', email));
  cc.forEach(email => data.append('cc[]', email));
  bcc.forEach(email => data.append('bcc[]', email));
  data.append('subject', subject);
  data.append('body', body);
  data.append('idempotency_key', idempotencyKey);
  files.forEach((file) => {
    if (isFileReference(file)) {
      data.append('ai_attachment_gids[]', file.gid);
    } else {
      data.append('attachments[]', file);
    }
  });
  return data;
}

export function isFileReference(file: EmailFile): file is EmailFileReference {
  return !(file instanceof File);
}

export function getFileName(file: EmailFile): string {
  return isFileReference(file) ? file.filename : file.name;
}

export function getFileSize(file: EmailFile): number {
  return isFileReference(file) ? file.byteSize : file.size;
}

// Round-tripped through Nylas as the OAuth `state` so the callback can return the broker to this
// contact and reopen the compose modal. Parsed server-side against /\Acompose_email:(\d+)\z/.
export function composeReturnState(contactId: number): string {
  return `compose_email:${contactId}`;
}

export function buildReauthUrl(authPath: string, provider: ProviderName, contactId: number): URL {
  return buildGrantUrl(authPath, provider, scopesFor(provider, true), composeReturnState(contactId));
}

// True when the page was reached via the post-reauth redirect and the mailbox can now send.
export function shouldAutoOpenCompose(search: string, sendEmail?: SendEmailConfig): boolean {
  if (!sendEmail?.enabled || !sendEmail.canSend || !sendEmail.path) return false;

  return new URLSearchParams(search).get('compose_email') === '1';
}
